import React, { useEffect, useMemo, useState } from "react";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import config from '../config.js';
import pinPrompt from '../utils/pinPrompt';
import openPdf from '../utils/openPdf';
import { assemblyCountFromPrimarySpace } from '../utils/assemblyMath';
import { desktopUi } from '../features/desktopUi';
import { confirmPickupFromQueue, pickupPalletLabel } from '../features/salesOrders/pickupQueue';
import { useShippingHubFlag } from '../features/shippingHub/hubFlag';
import ShippingHub from '../features/shippingHub/ShippingHub';
import { livePlanOrderIds } from '../features/shippingHub/soBoard';
import { workingAsUi } from '../features/operatorSession/workingAsUi';
import { liveSession } from '../features/operatorSession/session';
import { useShippingSigningFlags } from '../features/shippingHub/signingFlags';
import JobLoadBox, { newJobLoad } from '../features/shippingHub/JobLoadBox';
import {
  filterShippingLines, initialSelection, lineAssemblyPpq, lineClosedFor, loadCustomers as readCustomers,
  loadShippingLines as readShippingLines, makeLineKey, queueCustomers as customersInQueue, selectAllVisible,
  setAssemblySelected, setRowSelected, setShipQuantity, shipSelected, shippingManifestPath, sortRows,
} from '../features/shippingHub/shipByJob';
import {
  addLabelBatch, fetchBatchPartLabels, labelConfigFor, loadShipmentBatches as readShipmentBatches,
  loadShippedItems as readShippedItems, manifestBatchPath, NO_BATCH_TEXT, palletLabelsBatchPath, partLabelsBatchPath,
  removeLabelBatch, shippedQty, updateLabelBatch,
} from '../features/shippingHub/shipHistory';
import CoatingManagementPage from './CoatingManagementPage';

// Adjust to your environment:
const API_BASE = config.API_BASE_URL;

// Ship by job's and History's reads, rules and requests are features/shippingHub/shipByJob.js + shipHistory.js
// (2026-10-29: one copy with the phone Shipping screen). Assembly lines (ppq > 1) display + edit their quantities in
// FINISHED-ASSEMBLY units (lineAssemblyPpq); storage is primary-part-space.

// The Shipping page. With GET /features shipping_hub on (Phase 1, 2026-10-27) it is the Shipping hub
// (features/shippingHub/ShippingHub.jsx): Pack / Ready to ship / Ready for pickup / Out for delivery from the order
// board, Outsourcing (the coating desk in operator mode), Ship by job (the Ready to Ship queue below) and History
// (Shipped Items below), every PIN answered by the "Working as" session. Off: Ready to Ship / Awaiting Pickup /
// Shipped Items as before. The Build Shipment tab is gone (0 calls in 120 days; packing plans ship from the guide).
// GET /features shipping_signing (Phases 2-4, 2026-10-28) goes to the hub; its job_loads switch also gives Ship by job a
// load box (features/shippingHub/JobLoadBox): the ticked lines ship with the pallet they went on and the truck.
export default function ShippingQueuePage() {
  const hub = useShippingHubFlag();
  const signing = useShippingSigningFlags();
  const hubUi = useMemo(() => workingAsUi(desktopUi), []);
  const jobLoads = hub.enabled && signing.job_loads;
  const [jobLoad, setJobLoad] = useState(() => newJobLoad());
  // the hub's order board: sales orders with a live packing plan (their "Ship by job" rows are closed)
  const [liveSoIds, setLiveSoIds] = useState(null);
  // Shared states
  const [currentTab, setCurrentTab] = useState("queue");
  const [modalImage, setModalImage] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);

  // "Ready to Ship" tab states
  const [shippingLines, setShippingLines] = useState([]);
  const [selectedLines, setSelectedLines] = useState({});
  const [loadingLines, setLoadingLines] = useState(false);
  const [shipmentDate, setShipmentDate] = useState(new Date());
  const [productFilter, setProductFilter] = useState("");
  const [salesOrderFilter, setSalesOrderFilter] = useState("");
  const [queueCustomerFilter, setQueueCustomerFilter] = useState("");
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [manifestDate, setManifestDate] = useState(null);
  const [customerFilter, setCustomerFilter] = useState("");
  const [notifyCustomer, setNotifyCustomer] = useState(false); 

  // "Shipped Items" tab states
  const [shippedLines, setShippedLines] = useState([]);
  const [loadingShipped, setLoadingShipped] = useState(false);
  const [shippedStartDate, setShippedStartDate] = useState(null);
  const [shippedEndDate, setShippedEndDate] = useState(null);
  const [shippedCustomerId, setShippedCustomerId] = useState("");
  const [shippedProductId, setShippedProductId] = useState("");
  const [shippedCustomerPo, setShippedCustomerPo] = useState("");
  const [shippedCustomerSo, setShippedCustomerSo] = useState("");
  const [shippedSortField, setShippedSortField] = useState(null);
  const [shippedSortDirection, setShippedSortDirection] = useState("asc");
  const [selectedShippedLines, setSelectedShippedLines] = useState({});
  const [isLabelModalOpen, setIsLabelModalOpen] = useState(false);
  const [labelConfig, setLabelConfig] = useState([]);
  const [isPrinting, setIsPrinting] = useState(false);
  const [shippedViewMode, setShippedViewMode] = useState("shipment"); // "task" or "shipment"
  const [shipmentBatches, setShipmentBatches] = useState([]);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [batchSOFilter, setBatchSOFilter] = useState("");

  // "Awaiting Pickup" tab states
  const [pickupPallets, setPickupPallets] = useState([]);
  const [loadingPickup, setLoadingPickup] = useState(false);
  const [pickupRefreshKey, setPickupRefreshKey] = useState(0);
  const [pickupBusyId, setPickupBusyId] = useState(null);     // the row whose Confirm Pickup is running
  const [pickupMessage, setPickupMessage] = useState(null);   // {text, ok} from the last Confirm Pickup


  useEffect(() => {
    loadShippingLines();
    loadCustomers();
  }, []);

  // Load awaiting-pickup pallets on mount (so the tab's count badge shows before it is opened), whenever a tab is
  // opened, and when refresh fires.
  useEffect(() => {
    if (hub.enabled) return undefined; // the hub reads its own board
    let cancelled = false;
    (async () => {
      setLoadingPickup(true);
      try {
        const res = await fetch(`${API_BASE}/shipping/awaiting-pickup`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const body = await res.json();
        if (!cancelled) setPickupPallets(body.pallets || []);
      } catch (err) {
        if (!cancelled) setPickupPallets([]);
        console.error('Failed to load awaiting-pickup pallets:', err);
      } finally {
        if (!cancelled) setLoadingPickup(false);
      }
    })();
    return () => { cancelled = true; };
  }, [currentTab, pickupRefreshKey, hub.enabled]);

  // The SO page's own Confirm Pickup (features/salesOrders/palletActions.confirmPickup): load-out checklist (pull
  // the beacon, check the pallet off), then the operator's PIN, then one POST with load_checklist.
  async function handleConfirmPickupRow(pallet) {
    if (pickupBusyId != null) return;
    setPickupMessage(null);
    const r = await confirmPickupFromQueue({
      row: pallet,
      ui: desktopUi,
      setBusy: (on) => setPickupBusyId(on ? pallet.pallet_id : null),
      setMessage: () => {},
      reload: () => setPickupRefreshKey(k => k + 1),
    });
    if (r && r.message) setPickupMessage({ text: r.message, ok: !!r.done });
  }

  async function handlePrintPickupManifest(pallet) {
    if (!pallet.shipment_group_id) return;
    try {
      await openPdf(`${API_BASE}/reports/manifest/batch/${pallet.shipment_group_id}`);
    } catch (err) {
      alert(`Print manifest failed: ${err.message}`);
    }
  }

  async function handlePrintPickupLabels(pallet) {
    if (!pallet.shipment_group_id) return;
    try {
      await openPdf(`${API_BASE}/reports/pallet_labels/batch/${pallet.shipment_group_id}`);
    } catch (err) {
      alert(`Print labels failed: ${err.message}`);
    }
  }

  async function loadShippingLines() {
    setLoadingLines(true);
    const r = await readShippingLines(API_BASE);
    if (r.ok) {
      setShippingLines(r.lines);
      setSelectedLines(initialSelection(r.lines));
    }
    setLoadingLines(false);
  }

  async function loadCustomers() {
    setLoadingCustomers(true);
    const r = await readCustomers(API_BASE);
    if (r.ok) setCustomers(r.customers);
    setLoadingCustomers(false);
  }

  const handleSelectAllChange = (e) => {
    const { checked } = e.target;
    setSelectedLines(selectAllVisible(selectedLines, visibleShippingLines, checked, lineClosed));
  };

  const handleQuantityChange = (jobId, partId, taskId, newQtyStr, consumableId = null) => {
    setSelectedLines((prev) => setShipQuantity(prev, shippingLines, { jobId, partId, taskId, value: newQtyStr, consumableId }));
  };

  async function promptForUserPin() {
    // the hub: who ships is the Working-as session (a sign-in when no one is)
    if (hub.enabled) return hubUi.identity('ship');
    const pin = await pinPrompt("Enter your 4-digit PIN:");
    return pin ? pin.trim() : null;
  }

  /** The hub: a line whose order ships from its packing plan's package guide can't be shipped here. */
  function lineClosed(line) {
    return lineClosedFor(liveSoIds, line);
  }

  // Ship Selected: features/shippingHub/shipByJob.shipSelected (every rule, the PIN, finalize_batch, the load's manifest)
  const handleShipSelected = async () => {
    const loadBox = jobLoads ? jobLoad : null;
    const r = await shipSelected({
      apiBase: API_BASE,
      ui: hubUi,
      selected: selectedLines,
      lines: shippingLines,
      isClosed: lineClosed,
      shipDate: shipmentDate,
      notifyCustomer,
      jobLoad: loadBox,
      askPin: promptForUserPin,
    });
    if (!r.done) return;
    if (loadBox) setJobLoad(newJobLoad());
    loadShippingLines();
    setNotifyCustomer(false);
  };

  const openImageModal = (imgSrc) => setModalImage(imgSrc);
  const closeModal = () => setModalImage(null);

  const handlePrintManifest = () => {
    if (!manifestDate) return alert("Please select a date for the shipping manifest.");
    openPdf(`${API_BASE}${shippingManifestPath(manifestDate, customerFilter)}`);
  };

  const handleSortClick = (field) => {
    setSortField(prevField => {
      if (prevField === field) {
        setSortDirection(prevDir => (prevDir === "asc" ? "desc" : "asc"));
        return prevField;
      } else {
        setSortDirection("asc");
        return field;
      }
    });
  };

  const queueCustomers = useMemo(() => customersInQueue(shippingLines), [shippingLines]);

  const visibleShippingLines = useMemo(() => filterShippingLines(shippingLines, {
    product: productFilter, salesOrder: salesOrderFilter, customerId: queueCustomerFilter, sortField, sortDirection,
  }), [shippingLines, productFilter, salesOrderFilter, queueCustomerFilter, sortField, sortDirection]);

  function getAssemblyRowStyle(index, totalCount) {
    const style = { borderLeft: "2px solid #0d6efd", borderRight: "2px solid #0d6efd" };
    if (index === 0) style.borderTop = "2px solid #0d6efd";
    if (index === totalCount - 1) style.borderBottom = "2px solid #0d6efd";
    return style;
  }
  
  function renderAssemblyGroup(line) {
    const components = line.assembly_components || [];
    
    const mainAssemblyRow = renderPartRow(
      line,
      {
        part_id: line.part_id,
        product_id: line.product_id,
        description: line.description,
        // Parent assembly row shows the FINISHED-ASSEMBLY count (the editable
        // ship qty is in the same units); component rows below stay in pieces.
        quantity_ordered: assemblyCountFromPrimarySpace(
          line.quantity_ordered, lineAssemblyPpq(line)
        ),
        defects: line.defects,
        image_path: line.image_path
      },
      false,
      true
    );

    const componentRows = components.map((comp, index) => {
        const rowStyle = getAssemblyRowStyle(index, components.length);
        return renderPartRow(line, comp, true, false, rowStyle);
    });

    return [mainAssemblyRow, ...componentRows];
  }

  function renderPartRow(line, part, isComponent, isAssemblyHeader = false, rowStyle = {}) {
    const { part_id, consumable_id, product_id, description, quantity_ordered, defects, image_path } = part;
    
    // Pass consumable_id to key generator
    const uniqueKey = makeLineKey(line.job_id, part_id, line.task_id, consumable_id);
    const lineSel = selectedLines[uniqueKey] || { selected: false, finalShipped: 0 };
    const closed = lineClosed(line);

    let combinedStyle = {};
    if (isAssemblyHeader) {
      combinedStyle = { backgroundColor: "var(--jq-assembly-header-bg, #e8f7fc)", fontWeight: 'bold' };
    } else if (isComponent) {
      combinedStyle = { backgroundColor: "var(--jq-bg-subtle, #fafafa)", fontSize: "0.9em", paddingLeft: "2rem", ...rowStyle };
    }

    const checkboxHandler = (e) => {
      const isChecked = e.target.checked;
      setSelectedLines(prev => (isAssemblyHeader
        ? setAssemblySelected(prev, line, isChecked)
        : setRowSelected(prev, uniqueKey, isChecked)));
    };

    return (
      <tr key={uniqueKey} style={combinedStyle}>
        <td>
          <input
            type="checkbox"
            checked={lineSel.selected && !closed}
            onChange={checkboxHandler}
            disabled={isComponent || closed}
            title={closed ? "Ships from its package guide" : undefined}
          />
        </td>
        <td>{product_id}</td>
        <td>
          {line.sales_order_number || "-"}
          {closed && !isComponent && (
            <div className="small text-muted" data-testid="ships-from-guide">Ships from its package guide</div>
          )}
        </td>
        <td>{line.master_job_num}</td>
        <td>{description || "N/A"}</td>
        <td>{quantity_ordered != null ? quantity_ordered : 0}</td>
        <td>{defects || 0}</td>
        <td style={{ maxWidth: "110px" }}>
          <input
            type="number"
            min="0"
            value={lineSel.finalShipped}
            className="form-control"
            onChange={(e) => handleQuantityChange(line.job_id, part_id, line.task_id, e.target.value, consumable_id)}
            disabled={isComponent || closed}
          />
        </td>
        <td>
          {image_path ? (
            <img src={image_path} alt={description || "Part"} style={{ width: "60px", height: "60px", cursor: "pointer" }} className="border" onClick={() => openImageModal(image_path)} />
          ) : (
            <div className="border d-flex align-items-center justify-content-center text-muted" style={{ width: "60px", height: "60px", fontSize: "10px" }}>No Image</div>
          )}
        </td>
      </tr>
    );
  }

  function historyFilters() {
    return {
      startDate: shippedStartDate, endDate: shippedEndDate, customerId: shippedCustomerId, productId: shippedProductId,
      customerPo: shippedCustomerPo, customerSo: shippedCustomerSo, salesOrder: batchSOFilter,
    };
  }

  async function loadShippedItems() {
    setLoadingShipped(true);
    setSelectedShippedLines({});
    const r = await readShippedItems(API_BASE, historyFilters());
    if (r.ok) setShippedLines(r.rows);
    else alert(r.message);
    setLoadingShipped(false);
  }

  async function loadShipmentBatches() {
    setLoadingBatches(true);
    const r = await readShipmentBatches(API_BASE, historyFilters());
    if (r.ok) setShipmentBatches(r.rows);
    else alert(r.message);
    setLoadingBatches(false);
  }

  const handleShippedSortClick = (field) => {
    setShippedSortField(prevField => {
      if (prevField === field) {
        setShippedSortDirection(prevDir => (prevDir === "asc" ? "desc" : "asc"));
        return prevField;
      } else {
        setShippedSortDirection("asc");
        return field;
      }
    });
  };

  const visibleShippedLines = useMemo(
    () => sortRows(shippedLines, shippedSortField, shippedSortDirection),
    [shippedLines, shippedSortField, shippedSortDirection]
  );
  
  const handleShippedCheckboxChange = (taskId, checked) => {
    setSelectedShippedLines(prev => ({
      ...prev,
      [taskId]: checked,
    }));
  };

  const handleShippedSelectAllChange = (e) => {
    const { checked } = e.target;
    const newState = {};
    if (checked) {
      visibleShippedLines.forEach(line => {
        newState[line.task_id] = true;
      });
    }
    setSelectedShippedLines(newState);
  };

  const selectedShippedCount = useMemo(() => {
    return Object.values(selectedShippedLines).filter(Boolean).length;
  }, [selectedShippedLines]);

  const handleReprintBatch = (batchId) => {
    if (!batchId) {
        alert(NO_BATCH_TEXT);
        return;
    }
    openPdf(`${API_BASE}${manifestBatchPath(batchId)}`);
  };

  const handleOpenLabelModal = () => {
    setLabelConfig(labelConfigFor(visibleShippedLines, selectedShippedLines));
    setIsLabelModalOpen(true);
  };

  const handlePrintLabels = async () => {
    setIsPrinting(true);
    const r = await fetchBatchPartLabels(API_BASE, labelConfig);
    if (!r.ok) {
      alert(r.message);
      setIsPrinting(false);
      return;
    }
    const url = URL.createObjectURL(r.blob);
    window.open(url, "_blank");
    setIsLabelModalOpen(false);
    setSelectedShippedLines({});
    setIsPrinting(false);
  };

  // "Ready to Ship" (the hub's "Ship by job"): rows of an order with a live packing plan ship from its package guide.
  function renderQueueTab() {
    const who = jobLoads ? ((liveSession() || {}).name || null) : null;
    const shipLabel = jobLoads ? `Mark loaded${who ? ` as ${who}` : ''}` : 'Ship Selected';
    return (
      <>
        {jobLoads && <JobLoadBox apiBase={API_BASE} value={jobLoad} onChange={setJobLoad} />}
        <div className="card mb-4">
          <div className="card-body">
            <h5 className="card-title mb-3">Generate Shipping Manifest</h5>
            <div className="row g-3 align-items-end">
              <div className="col-md-4">
                <label className="form-label">Manifest Date:</label>
                <ReactDatePicker selected={manifestDate} onChange={setManifestDate} dateFormat="yyyy-MM-dd" className="form-control" placeholderText="Click to select a date" />
              </div>
              <div className="col-md-4">
                <label className="form-label">Customer Filter:</label>
                <select className="form-select" disabled={loadingCustomers} value={customerFilter} onChange={(e) => setCustomerFilter(e.target.value)}>
                  <option value="">-- All Customers --</option>
                  {customers.map((cust) => (<option key={cust.customer_id} value={cust.customer_id}>{cust.name}</option>))}
                </select>
              </div>
              <div className="col-md-4">
                <button className="btn btn-primary w-100" onClick={handlePrintManifest} disabled={loadingCustomers}>Print Shipping Manifest</button>
              </div>
            </div>
          </div>
        </div>
        <div className="row mb-3">
          <div className="col-sm-4">
            <label className="form-label">Filter by Product ID:</label>
            <input type="text" className="form-control" value={productFilter} onChange={(e) => setProductFilter(e.target.value)} placeholder="Type to filter..." />
          </div>
          <div className="col-sm-4">
            <label className="form-label">Filter by Sales Order:</label>
            <input type="text" className="form-control" value={salesOrderFilter} onChange={(e) => setSalesOrderFilter(e.target.value)} placeholder="SO-100..." />
          </div>
          <div className="col-sm-4">
            <label className="form-label">Filter by Customer:</label>
            <select className="form-select" value={queueCustomerFilter} onChange={(e) => setQueueCustomerFilter(e.target.value)}>
              <option value="">All Customers</option>
              {queueCustomers.map((cust) => (<option key={cust.customer_id} value={cust.customer_id}>{cust.name}</option>))}
            </select>
          </div>
        </div>

        {loadingLines ? (<div className="alert alert-info">Loading shipping lines...</div>) : 
         visibleShippingLines.length === 0 ? (<div className="alert alert-secondary">No lines found in the Shipping Queue.</div>) : 
         (
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Ready to Ship</h5>
              <div className="d-flex align-items-center">
                <div className="form-check form-switch me-4">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="notifySwitch"
                    checked={notifyCustomer}
                    onChange={(e) => setNotifyCustomer(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="notifySwitch">
                    Email Customer Manifest
                  </label>
                </div>
                
                <div className="me-3">
                  <label className="form-label mb-0 me-2 fw-bold">Shipment Date:</label>
                  <ReactDatePicker selected={shipmentDate} onChange={setShipmentDate} dateFormat="yyyy-MM-dd" className="form-control form-control-sm d-inline-block" style={{width: 'auto'}} />
                </div>
                <button className="btn btn-success" style={jobLoads ? { minHeight: 48 } : undefined} onClick={handleShipSelected}>{shipLabel}</button>
              </div>
            </div>
            <div className="card-body p-0">
              <table className="table table-striped table-hover mb-0">
                <thead>
                  <tr>
                    <th style={{ width: "50px" }}><input type="checkbox" onChange={handleSelectAllChange} aria-label="Select All" /></th>
                    <th style={{ cursor: "pointer" }} onClick={() => handleSortClick("product_id")}>Product ID {sortField === "product_id" && (sortDirection === "asc" ? "▲" : "▼")}</th>
                    <th style={{ cursor: "pointer" }} onClick={() => handleSortClick("sales_order_number")}>Sales Order ...</th>
                    <th style={{ cursor: "pointer" }} onClick={() => handleSortClick("master_job_num")}>Master Job {sortField === "master_job_num" && (sortDirection === "asc" ? "▲" : "▼")}</th>
                    <th>Description</th>
                    <th style={{ cursor: "pointer" }} onClick={() => handleSortClick("quantity_ordered")}>Quantity {sortField === "quantity_ordered" && (sortDirection === "asc" ? "▲" : "▼")}</th>
                    <th>Defects</th>
                    <th>Final Ship Qty</th>
                    <th>Thumbnail</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleShippingLines.map((line) => line.assembly_id ? 
                      renderAssemblyGroup(line) : 
                      renderPartRow(line, { part_id: line.part_id, product_id: line.product_id, description: line.description, quantity_ordered: line.quantity_ordered, defects: line.defects, image_path: line.image_path }, false)
                  )}
                </tbody>
              </table>
            </div>
            <div className="card-footer d-flex justify-content-end align-items-center">
               <div className="d-flex align-items-center">
                <div className="form-check form-switch me-4">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="notifySwitchFooter"
                    checked={notifyCustomer}
                    onChange={(e) => setNotifyCustomer(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="notifySwitchFooter">
                    Email Customer Manifest
                  </label>
                </div>
                <div className="me-3"><label className="form-label mb-0 me-2 fw-bold">Shipment Date:</label><ReactDatePicker selected={shipmentDate} onChange={setShipmentDate} dateFormat="yyyy-MM-dd" className="form-control form-control-sm d-inline-block" style={{width: 'auto'}} /></div>
                <button className="btn btn-success" style={jobLoads ? { minHeight: 48 } : undefined} onClick={handleShipSelected}>{shipLabel}</button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // "Awaiting Pickup" (the page without the hub).
  function renderPickupTab() {
    return (
      <div className="card mb-4">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="card-title mb-0">
              <i className="bi bi-box-seam me-2 text-info"></i>
              Awaiting Customer Pickup
            </h5>
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setPickupRefreshKey(k => k + 1)}
              disabled={loadingPickup}
            >
              <i className="bi bi-arrow-clockwise me-1"></i>Refresh
            </button>
          </div>
          {pickupMessage && (
            <div className={`alert ${pickupMessage.ok ? 'alert-success' : 'alert-danger'} py-2 d-flex justify-content-between align-items-start`} role="status">
              <span>{pickupMessage.text}</span>
              <button type="button" className="btn-close ms-2" aria-label="Dismiss" onClick={() => setPickupMessage(null)}></button>
            </div>
          )}
          {loadingPickup && (
            <div className="text-center p-4">
              <div className="spinner-border text-info"></div>
            </div>
          )}
          {!loadingPickup && pickupPallets.length === 0 && (
            <div className="alert alert-info mb-0">
              <i className="bi bi-info-circle me-2"></i>
              No pallets currently awaiting customer pickup. When you mark a sealed pallet
              "Ready for Pickup" on a Sales Order, it appears here until the customer arrives.
            </div>
          )}
          {!loadingPickup && pickupPallets.length > 0 && (
            <div className="table-responsive">
              <table className="table table-hover align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Our SO #</th>
                    <th>Cust SO #</th>
                    <th>Cust PO #</th>
                    <th>Customer</th>
                    <th>Contact</th>
                    <th>Pallet</th>
                    <th>Staged</th>
                    <th>Age</th>
                    <th>Staged by</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pickupPallets.map(p => {
                    const days = p.days_since_staged || 0;
                    const isStale = days >= 3;
                    const isVeryStale = days >= 7;
                    const ageBadgeClass = isVeryStale ? 'bg-danger' : isStale ? 'bg-warning text-dark' : 'bg-light text-dark';
                    const ageLabel = days < 1 ? `${Math.round(days * 24)}h` : `${Math.floor(days)}d`;
                    return (
                      <tr key={p.pallet_id} className={isVeryStale ? 'table-danger' : ''}>
                        <td>
                          <a href={`/sales-orders/${p.sales_order_id}`} className="fw-bold text-decoration-none">
                            {p.order_number}
                          </a>
                        </td>
                        <td className="small">
                          {p.customer_so ? <span className="fw-semibold">{p.customer_so}</span> : <span className="text-muted">—</span>}
                        </td>
                        <td className="small">
                          {p.customer_po ? <span className="fw-semibold">{p.customer_po}</span> : <span className="text-muted">—</span>}
                        </td>
                        <td>
                          <div className="fw-bold">{p.customer_name}</div>
                          {p.customer_short_code && <div className="small text-muted">{p.customer_short_code}</div>}
                        </td>
                        <td className="small">
                          {p.customer_phone && <div><i className="bi bi-telephone me-1"></i>{p.customer_phone}</div>}
                          {p.customer_email && <div className="text-muted text-truncate" style={{ maxWidth: 200 }}><i className="bi bi-envelope me-1"></i>{p.customer_email}</div>}
                        </td>
                        <td>
                          <span className="fw-bold" style={{ fontFamily: 'monospace' }}>
                            {pickupPalletLabel(p)}
                          </span>
                        </td>
                        <td className="small">
                          {p.staged_at ? new Date(p.staged_at).toLocaleString() : '—'}
                        </td>
                        <td>
                          <span className={`badge ${ageBadgeClass}`}>{ageLabel}</span>
                        </td>
                        <td className="small text-muted">{p.staged_by || '—'}</td>
                        <td className="text-end">
                          <div className="btn-group">
                            <button className="btn btn-sm btn-outline-secondary"
                                    onClick={() => handlePrintPickupLabels(p)}
                                    title="Reprint labels">
                              <i className="bi bi-printer"></i>
                            </button>
                            <button className="btn btn-sm btn-outline-secondary"
                                    onClick={() => handlePrintPickupManifest(p)}
                                    title="Reprint manifest (give to customer at handoff)">
                              <i className="bi bi-file-earmark-text"></i>
                            </button>
                            <button className="btn btn-sm btn-success"
                                    onClick={() => handleConfirmPickupRow(p)}
                                    disabled={pickupBusyId != null}
                                    title="Customer is here — check the pallet off as it is loaded, then confirm pickup">
                              {pickupBusyId === p.pallet_id
                                ? <span className="spinner-border spinner-border-sm me-1"></span>
                                : <i className="bi bi-person-check me-1"></i>}
                              Confirm Pickup
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  }

  // "Shipped Items" (the hub's "History").
  function renderShippedTab() {
    return (
      <>
        {/* View Mode Toggle */}
        <div className="btn-group mb-3">
          <button className={`btn ${shippedViewMode === "shipment" ? "btn-primary" : "btn-outline-primary"}`} onClick={() => setShippedViewMode("shipment")}>By Shipment</button>
          <button className={`btn ${shippedViewMode === "task" ? "btn-primary" : "btn-outline-primary"}`} onClick={() => setShippedViewMode("task")}>By Task</button>
        </div>

        {/* ---- BY SHIPMENT VIEW ---- */}
        {shippedViewMode === "shipment" && (
          <>
            <div className="card mb-4">
              <div className="card-body">
                <h5 className="card-title mb-3">Find Shipment Batches</h5>
                <div className="row g-3 mb-3">
                  <div className="col-md-3"><label className="form-label">Start Date:</label><ReactDatePicker selected={shippedStartDate} onChange={setShippedStartDate} dateFormat="yyyy-MM-dd" className="form-control" placeholderText="Start date" /></div>
                  <div className="col-md-3"><label className="form-label">End Date:</label><ReactDatePicker selected={shippedEndDate} onChange={setShippedEndDate} dateFormat="yyyy-MM-dd" className="form-control" placeholderText="End date" /></div>
                  <div className="col-md-3"><label className="form-label">Customer:</label><select className="form-select" disabled={loadingCustomers} value={shippedCustomerId} onChange={(e) => setShippedCustomerId(e.target.value)}><option value="">-- All --</option>{customers.map((cust) => (<option key={cust.customer_id} value={cust.customer_id}>{cust.name}</option>))}</select></div>
                  <div className="col-md-3"><label className="form-label">Sales Order #:</label><input type="text" className="form-control" value={batchSOFilter} onChange={(e) => setBatchSOFilter(e.target.value)} placeholder="Filter by SO#" /></div>
                </div>
                <div className="row g-3">
                  <div className="col-md-12 d-flex align-items-end justify-content-end"><button className="btn btn-primary" onClick={loadShipmentBatches}>Search Shipments</button></div>
                </div>
              </div>
            </div>
            {loadingBatches ? (<div className="alert alert-info">Loading shipment batches...</div>) :
             shipmentBatches.length === 0 ? (<div className="alert alert-secondary">No shipment batches found. Use the filters above and click Search.</div>) :
             (
              <div className="card">
                <div className="card-header">
                  <h5 className="mb-0">Shipment Batches ({shipmentBatches.length})</h5>
                </div>
                <div className="card-body p-0">
                  <table className="table table-striped table-hover mb-0">
                    <thead>
                      <tr>
                        <th>Ship Date</th>
                        <th>Customer</th>
                        <th>SO #</th>
                        <th>Customer PO</th>
                        <th>Cust SO</th>
                        <th>Parts</th>
                        <th className="text-center">Qty</th>
                        <th className="text-center">Containers</th>
                        <th className="text-end">Reprint Documents</th>
                      </tr>
                    </thead>
                    <tbody>
                      {shipmentBatches.map((batch) => (
                        <tr key={batch.shipment_group_id}>
                          <td>{batch.ship_date || "N/A"}</td>
                          <td>{batch.customer_name}</td>
                          <td>{batch.sales_order_number || "—"}</td>
                          <td>{batch.customer_po || "—"}</td>
                          <td>{batch.customer_so || "—"}</td>
                          <td className="text-truncate" style={{ maxWidth: "250px" }} title={batch.product_ids}>{batch.product_ids}</td>
                          <td className="text-center">{batch.total_qty}</td>
                          <td className="text-center">
                            {batch.pallet_count || "—"}
                            {batch.container_types && <div className="text-muted small">{batch.container_types}</div>}
                          </td>
                          <td className="text-end">
                            <div className="btn-group btn-group-sm">
                              <button
                                className="btn btn-outline-primary"
                                onClick={() => openPdf(`${API_BASE}${manifestBatchPath(batch.shipment_group_id)}`)}
                                title="Reprint Manifest"
                              >
                                Manifest
                              </button>
                              {batch.has_pallets && (
                                <>
                                  <button
                                    className="btn btn-outline-info"
                                    onClick={() => openPdf(`${API_BASE}${palletLabelsBatchPath(batch.shipment_group_id)}`)}
                                    title="Reprint Container Labels"
                                  >
                                    Container Labels
                                  </button>
                                  <button
                                    className="btn btn-outline-secondary"
                                    onClick={() => openPdf(`${API_BASE}${partLabelsBatchPath(batch.shipment_group_id)}`)}
                                    title="Reprint Part Labels"
                                  >
                                    Part Labels
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        {/* ---- BY TASK VIEW (original) ---- */}
        {shippedViewMode === "task" && (
          <>
            <div className="card mb-4">
              <div className="card-body">
                <h5 className="card-title mb-3">View Shipped Items</h5>
                <div className="row g-3 mb-3">
                  <div className="col-md-3"><label className="form-label">Start Date:</label><ReactDatePicker selected={shippedStartDate} onChange={setShippedStartDate} dateFormat="yyyy-MM-dd" className="form-control" placeholderText="Start date" /></div>
                  <div className="col-md-3"><label className="form-label">End Date:</label><ReactDatePicker selected={shippedEndDate} onChange={setShippedEndDate} dateFormat="yyyy-MM-dd" className="form-control" placeholderText="End date" /></div>
                  <div className="col-md-3"><label className="form-label">Customer:</label><select className="form-select" disabled={loadingCustomers} value={shippedCustomerId} onChange={(e) => setShippedCustomerId(e.target.value)}><option value="">-- All --</option>{customers.map((cust) => (<option key={cust.customer_id} value={cust.customer_id}>{cust.name}</option>))}</select></div>
                  <div className="col-md-3"><label className="form-label">Product ID:</label><input type="text" className="form-control" value={shippedProductId} onChange={(e) => setShippedProductId(e.target.value)} placeholder="Filter by Product ID" /></div>
                </div>
                <div className="row g-3">
                  <div className="col-md-3"><label className="form-label">Customer PO:</label><input type="text" className="form-control" value={shippedCustomerPo} onChange={(e) => setShippedCustomerPo(e.target.value)} placeholder="Filter by Customer PO" /></div>
                  <div className="col-md-3"><label className="form-label">Cust SO:</label><input type="text" className="form-control" value={shippedCustomerSo} onChange={(e) => setShippedCustomerSo(e.target.value)} placeholder="Filter by Cust SO" /></div>
                  <div className="col-md-9 d-flex align-items-end justify-content-end"><button className="btn btn-primary" onClick={loadShippedItems}>Load Shipped Items</button></div>
                </div>
              </div>
            </div>
            {loadingShipped ? (<div className="alert alert-info">Loading shipped items...</div>) :
             shippedLines.length === 0 ? (<div className="alert alert-secondary">No shipped items found.</div>) :
             (
              <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">Shipped Items</h5>
                  <button
                    className="btn btn-info"
                    onClick={handleOpenLabelModal}
                    disabled={selectedShippedCount === 0}
                  >
                    Print Labels for Selected ({selectedShippedCount})
                  </button>
                </div>
                <div className="card-body p-0">
                  <table className="table table-striped table-hover mb-0">
                    <thead>
                      <tr>
                        <th style={{ width: "50px" }}>
                          <input
                            type="checkbox"
                            onChange={handleShippedSelectAllChange}
                            checked={visibleShippedLines.length > 0 && selectedShippedCount === visibleShippedLines.length}
                            aria-label="Select All Shipped"
                          />
                        </th>
                        <th style={{ cursor: "pointer" }} onClick={() => handleShippedSortClick("shipped_date")}>Date Shipped {shippedSortField === "shipped_date" && (shippedSortDirection === "asc" ? "▲" : "▼")}</th>
                        <th style={{ cursor: "pointer" }} onClick={() => handleShippedSortClick("product_id")}>Product ID {shippedSortField === "product_id" && (shippedSortDirection === "asc" ? "▲" : "▼")}</th>
                        <th>Master Job</th>
                        <th>Description</th>
                        <th style={{ cursor: "pointer" }} onClick={() => handleShippedSortClick("quantity_shipped")}>Quantity Shipped {shippedSortField === "quantity_shipped" && (shippedSortDirection === "asc" ? "▲" : "▼")}</th>
                        <th>Thumbnail</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleShippedLines.map((line) => (
                        <tr key={line.task_id}>
                          <td>
                            <input
                              type="checkbox"
                              checked={!!selectedShippedLines[line.task_id]}
                              onChange={(e) => handleShippedCheckboxChange(line.task_id, e.target.checked)}
                            />
                          </td>
                          <td>{line.shipped_date || "N/A"}</td>
                          <td>{line.product_id}</td>
                          <td>{line.master_job_num}</td>
                          <td>{line.description || "N/A"}</td>
                          <td>{shippedQty(line)}</td>
                          <td>
                            {line.image_path ? (
                              <img src={line.image_path} alt={line.description || "Part"} style={{ width: "60px", height: "60px", cursor: "pointer" }} className="border" onClick={() => openImageModal(line.image_path)} />
                            ) : (
                              <div className="border d-flex align-items-center justify-content-center text-muted" style={{ width: "60px", height: "60px", fontSize: "10px" }}>No Image</div>
                            )}
                          </td>
                          <td>
                              <button
                                  className="btn btn-sm btn-outline-secondary"
                                  disabled={!line.shipment_group_id}
                                  onClick={() => handleReprintBatch(line.shipment_group_id)}
                                  title="Reprint the manifest for this shipment batch"
                              >
                                  <i className="bi bi-printer"></i> Reprint Manifest
                              </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </>
    );
  }

  function renderModals() {
    return (
      <>
        {modalImage && (
          <div onClick={closeModal} style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}>
            <div onClick={(e) => e.stopPropagation()} className="bg-white rounded p-3" style={{ maxWidth: "90%", maxHeight: "90%", overflow: "auto" }}>
              <img src={modalImage} alt="Enlarged part" style={{ maxWidth: "100%", maxHeight: "80vh" }} />
              <div className="text-end mt-2"><button className="btn btn-secondary" onClick={closeModal}>Close</button></div>
            </div>
          </div>
        )}

        {isLabelModalOpen && (
          <LabelConfigurationModal
            isOpen={isLabelModalOpen}
            onClose={() => setIsLabelModalOpen(false)}
            config={labelConfig}
            setConfig={setLabelConfig}
            onPrint={handlePrintLabels}
            isPrinting={isPrinting}
          />
        )}
      </>
    );
  }

  if (hub.enabled) {
    return (
      <>
        <ShippingHub
          apiBase={API_BASE}
          ui={hubUi}
          signing={signing}
          onBoard={(board) => setLiveSoIds(livePlanOrderIds(board))}
          renderOutsourcing={() => <CoatingManagementPage mode="operator" ui={hubUi} />}
          renderByJob={() => renderQueueTab()}
          renderHistory={() => renderShippedTab()}
        />
        {renderModals()}
      </>
    );
  }

  return (
    <div className="container my-4">
      <h1 className="mb-4 text-primary">Shipping Queue</h1>
      <ul className="nav nav-tabs mb-4">
        <li className="nav-item">
          <button className={`nav-link ${currentTab === "queue" ? "active" : ""}`} onClick={() => setCurrentTab("queue")}>Ready to Ship</button>
        </li>
        <li className="nav-item">
          <button className={`nav-link ${currentTab === "pickup" ? "active" : ""}`} onClick={() => setCurrentTab("pickup")}>
            <i className="bi bi-box-seam me-1"></i>Awaiting Pickup
            {pickupPallets.length > 0 && currentTab !== 'pickup' && (
              <span className="badge bg-info text-dark ms-2">{pickupPallets.length}</span>
            )}
          </button>
        </li>
        <li className="nav-item">
          <button className={`nav-link ${currentTab === "shipped" ? "active" : ""}`} onClick={() => setCurrentTab("shipped")}>Shipped Items</button>
        </li>
      </ul>

      {currentTab === 'pickup' && renderPickupTab()}

      {currentTab === "queue" && renderQueueTab()}

      {currentTab === "shipped" && renderShippedTab()}

      {renderModals()}
    </div>
  );
}

function LabelConfigurationModal({ isOpen, onClose, config, setConfig, onPrint, isPrinting }) {
  
  const addBatch = (partId) => setConfig(prevConfig => addLabelBatch(prevConfig, partId));
  const removeBatch = (partId, batchId) => setConfig(prevConfig => removeLabelBatch(prevConfig, partId, batchId));
  const updateBatch = (partId, batchId, field, value) => setConfig(prevConfig => updateLabelBatch(prevConfig, partId, batchId, field, value));

  return (
    <div 
      style={{ 
        position: "fixed", top: 0, left: 0, right: 0, bottom: 0, 
        backgroundColor: "rgba(0,0,0,0.5)", 
        display: "flex", alignItems: "center", justifyContent: "center", 
        zIndex: 10000 
      }}
      onClick={onClose}
    >
      <div 
        style={{ 
          backgroundColor: "var(--jq-bg-card, white)", 
          borderRadius: "8px", 
          padding: "20px", 
          width: "90%", 
          maxWidth: "700px",
          maxHeight: "90vh",
          overflowY: "auto"
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0">Configure Part Labels</h4>
          <button type="button" className="btn-close" onClick={onClose}></button>
        </div>
        
        <div className="modal-body">
          {config.map(part => (
            <div key={part.part_id} className="card mb-3">
              <div className="card-header">
                <strong>{part.product_id}</strong>: {part.description}
              </div>
              <div className="card-body">
                {part.batches.map(batch => (
                  <div key={batch.id} className="row g-2 align-items-center mb-2">
                    <div className="col">
                      <label className="form-label">Qty on Label</label>
                      <input
                        type="number"
                        className="form-control"
                        value={batch.qty}
                        onChange={(e) => updateBatch(part.part_id, batch.id, 'qty', e.target.value)}
                        min="1"
                      />
                    </div>
                    <div className="col">
                      <label className="form-label">Number of Labels</label>
                      <input
                        type="number"
                        className="form-control"
                        value={batch.count}
                        onChange={(e) => updateBatch(part.part_id, batch.id, 'count', e.target.value)}
                        min="1"
                      />
                    </div>
                    <div className="col-auto d-flex align-items-end">
                      <button 
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => removeBatch(part.part_id, batch.id)}
                        disabled={part.batches.length <= 1}
                      >
                        X
                      </button>
                    </div>
                  </div>
                ))}
                <button 
                  className="btn btn-outline-primary btn-sm mt-2"
                  onClick={() => addBatch(part.part_id)}
                >
                  + Add Batch (e.g., for another box)
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button 
            type="button" 
            className="btn btn-primary" 
            onClick={onPrint}
            disabled={isPrinting}
          >
            {isPrinting ? "Printing..." : "Print Labels"}
          </button>
        </div>
      </div>
    </div>
  );
}